import type { RefObject } from 'react'

import type WebsocketService from 'storage/service-worker/websocket-service'
import type { EventTrack } from 'types/player'

export enum ErrorStates {
  ON_PLAY = 'onPlay',
  ON_SWITCH_TRACK = 'onSwitchTrack',
  ON_MUTE_TOGGLE = 'onMuteToggle',
  ON_STABILITY_ERROR = 'onStabilityError',
}

export type PlayProperties = {
  trackId: string
  trackCurrentProgress: number
  trackDuration: number
  isPlaying: boolean
}

export type UseAudioPlayerProps = {
  /**
   * The track to play
   */
  track: EventTrack | null
  /**
   * The start time of the event
   */
  startTime: number
  /**
   * The websocket service
   */

  socket: WebsocketService
  /**
   * When the play button is hit
   */
  onPlay?: (properties: PlayProperties) => void
  /**
   * When the track is switched to another
   */
  onPlayNewTrack?: (oldTrackId: string, trackId: string) => void
  /**
   * When there is an active track but it has
   * already completed when we hit play.
   */
  onActiveFinishedAtInit?: (trackId: string) => void
  /**
   * When the track has finished playing
   */
  onActiveFinished?: (trackId: string) => void
  /**
   * When the mute/unmute button is pressed
   */
  onMuteToggle?: (isMuted: boolean) => void
  /**
   * When the track fails to play
   */
  onError?: (trackId: string, circumstance: ErrorStates) => void
}

export type UseSingleTrackAudioPlayerReturn = {
  /** Pass to the <audio> element as ref so the hook can control playback and sync. */
  audioRef: RefObject<HTMLAudioElement>
  /** Web Audio source node (element → graph). Pass to visualizer/analyzer to connect in the chain. */
  sourceNode: MediaElementAudioSourceNode | null
  /** Web Audio context, created on first play. Pass to visualizer or use for analytics. */
  audioCtx: AudioContext | null
  /** Gain node used for volume and mute. Pass to visualizer so analyzer connects before gain. */
  gainNode: GainNode | null
  /** Call when the user taps mute/unmute (e.g. play button when hasPlayed). */
  handleMuteToggle: () => Promise<void>
  /** Call when the user taps play for the first time (e.g. play button when !hasPlayed). */
  handlePlay: () => Promise<void>
  /** True after play has been triggered at least once. Use to show play vs mute UI and to gate visualizer. */
  hasPlayed: boolean
  /** Current mute state. Use for mute/unmute icon and pass to visualizer for styling. */
  isMuted: boolean
  /** True while playhead sync is stabilizing (up to ~4s). Use for loading/processing UI and to hide visualizer. */
  isWarmingUp: boolean
  /** Set when playback fails (on play, switch, mute, or stability). Use for error UI or redirect. */
  error: ErrorStates | null
}
